import {
  Box,
  Text,
  Link,
  IconButton,
  Flex,
  Stack,
  useColorMode,
  useDisclosure,
} from '@chakra-ui/react';
import { MoonIcon, SunIcon, CloseIcon, HamburgerIcon } from '@chakra-ui/icons';

const Logo = ({ colorMode, ...props }) => {
  const glow = `0 0 12px ${
    colorMode === 'light'
      ? 'rgba(252, 74, 26, 0.4)'
      : 'rgba(255, 122, 77, 0.65)'
  }`;

  return (
    <Link
      href="/"
      _hover={{ textDecoration: 'none' }}
      _focusVisible={{ boxShadow: 'outline' }}
      {...props}
    >
      <Text
        fontWeight="extrabold"
        fontSize="lg"
        color={`mode.${colorMode}.text`}
        transition="color 180ms ease, text-shadow 180ms ease"
        _hover={{
          color: `mode.${colorMode}.accent`,
          textShadow: glow,
        }}
      >
        Dara Heng
      </Text>
    </Link>
  );
};

const MenuToggle = ({ toggle, isOpen, svgColor }) => {
  return (
    <IconButton
      display={{ base: 'inline-flex', md: 'none' }}
      aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
      aria-expanded={isOpen}
      aria-controls="mobile-navigation"
      icon={
        isOpen ? (
          <CloseIcon color={svgColor} w={3} h={3} />
        ) : (
          <HamburgerIcon color={svgColor} w={5} h={5} />
        )
      }
      size="sm"
      variant="ghost"
      onClick={toggle}
      _hover={{ bg: 'transparent' }}
    />
  );
};

const MenuItem = ({ children, to = '/', colorMode, ...rest }) => {
  const glow = `0 0 12px ${
    colorMode === 'light'
      ? 'rgba(252, 74, 26, 0.4)'
      : 'rgba(255, 122, 77, 0.65)'
  }`;
  const interactiveStyles = {
    color: `mode.${colorMode}.accent`,
    fontWeight: 'bold',
    textShadow: glow,
  };

  return (
    <Link
      href={to}
      display="block"
      _hover={{ textDecoration: 'none' }}
      _focusVisible={{
        boxShadow: 'outline',
        '& > p': interactiveStyles,
      }}
    >
      <Text
        display="block"
        fontSize="sm"
        fontWeight="medium"
        color={`mode.${colorMode}.subtext`}
        transition="color 180ms ease, text-shadow 180ms ease, font-weight 180ms ease"
        _hover={interactiveStyles}
        {...rest}
      >
        {children}
      </Text>
    </Link>
  );
};

const MenuLinks = ({ colorMode, isMobile = false }) => {
  return (
    <Stack
      spacing={isMobile ? 1 : 9}
      align={isMobile ? 'stretch' : 'center'}
      direction={isMobile ? 'column' : 'row'}
    >
      <MenuItem
        to="/experience"
        colorMode={colorMode}
        fontSize={isMobile ? 'md' : 'sm'}
        py={isMobile ? 3 : 0}
      >
        Experience
      </MenuItem>
      <MenuItem
        to="/projects"
        colorMode={colorMode}
        fontSize={isMobile ? 'md' : 'sm'}
        py={isMobile ? 3 : 0}
      >
        Projects
      </MenuItem>
      <MenuItem
        to="/lifemotto"
        colorMode={colorMode}
        fontSize={isMobile ? 'md' : 'sm'}
        py={isMobile ? 3 : 0}
      >
        Life Motto
      </MenuItem>
    </Stack>
  );
};

const NavBarContainer = ({ children, colorMode, ...props }) => {
  return (
    <Box
      w="100%"
      borderBottom="1px solid"
      borderColor={`mode.${colorMode}.border`}
      mb={8}
    >
      <Box
        as="nav"
        w="100%"
        maxW="1100px"
        mx="auto"
        px={{ base: 4, md: 8 }}
        py={4}
        {...props}
      >
        {children}
      </Box>
    </Box>
  );
};

const NavBar = (props) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onToggle } = useDisclosure();

  const svgColor = `mode.${colorMode}.text`;

  return (
    <NavBarContainer colorMode={colorMode} {...props}>
      <Flex align="center" justify="space-between" minH={8}>
        <Logo colorMode={colorMode} />
        <Flex align="center" gap={{ base: 1, md: 2 }}>
          <Box display={{ base: 'none', md: 'block' }}>
            <MenuLinks colorMode={colorMode} />
          </Box>
          <MenuToggle toggle={onToggle} svgColor={svgColor} isOpen={isOpen} />
          <IconButton
            aria-label={
              colorMode === 'light'
                ? 'Switch to dark mode'
                : 'Switch to light mode'
            }
            icon={
              colorMode === 'light' ? (
                <MoonIcon color={svgColor} w={4} h={4} />
              ) : (
                <SunIcon color={svgColor} w={4} h={4} />
              )
            }
            size="sm"
            ml={{ base: 0, md: 2 }}
            onClick={toggleColorMode}
            variant="ghost"
            _hover={{ bg: 'transparent' }}
          />
        </Flex>
      </Flex>
      <Box
        id="mobile-navigation"
        display={{ base: isOpen ? 'block' : 'none', md: 'none' }}
        mt={4}
        pt={2}
        borderTop="1px solid"
        borderColor={`mode.${colorMode}.border`}
      >
        <MenuLinks colorMode={colorMode} isMobile />
      </Box>
    </NavBarContainer>
  );
};

export default NavBar;
